<template>
  <VDialogV3
    position="right"
    width="600px"
    :entity="localOrder"
    @submit="onSubmit"
    :submit-disabled="isSourcesLoading ? 'Загружаются данные' : null"
    @close="$emit('close')"
  >
    <section>
      <header>Категория заказа</header>
      <VSelectV2
        v-model="localOrder.requisites.orderCategory"
        required
        :data="orderCategoriesOptions"
      />
    </section>

    <template v-if="localOrder.requisites.isPreorder === false">
      <hr>

      <section>
        <header>Образец</header>
        <VSwitchV2
          v-model="localOrder.requisites.isSamples"
          layout="vertical"
          theme="slider"

          :depends-on="() => localOrder.requisites.orderCategory"
          :default="(orderCategory) => orderCategory.id === 'freeSamples'"
        />

        <VSwitch
          v-if="localOrder.requisites.isSamples"
          v-model="localOrder.requisites.isForTestsOnly"
          label="Под производственный план"
          layout="vertical"
          :checked-value="false"
          :unchecked-value="true"
          theme="slider"
        />
      </section>
    </template>

    <hr>

    <section>
      <header>Основное</header>
      <VSelectV2
        v-model="localOrder.requisites.seller"
        placeholder="Название или ID"
        label="Продавец"
        required
        :data="({pattern}) => PartnersService.searchSellers(pattern)"
      />

      <div class="select-company-wrapper">
        <VSelectV2
          v-model="localOrder.requisites.sellerCompany"
          label="Юр. лицо"
          :data="sellerCompanySource"
          :disabled="localOrder.requisites.isOfferBased"
          required
        />

        <div class="inn">
          {{ localOrder.requisites.sellerCompany?.innDescription }}
        </div>
      </div>

      <VSelectV2
        label="Склад отправки"
        required
        :disabled="!hasSeller"
        v-model="localOrder.requisites.sellerStore"
        :data="sellerStoreSource"
      />
    </section>

    <section>
      <VSelectV2
        label="Покупатель"
        v-model="localOrder.requisites.buyer"
        disabled
        :data="[$core.currentPartner]"
      />

      <div class="select-company-wrapper">
        <VSelectV2
          label="Юр. лицо"
          option-description="innDescription"
          v-model="localOrder.requisites.buyerCompany"
          :disabled="localOrder.requisites.isOfferBased"
          :data="buyerCompanySource"
        />

        <div
          v-if="localOrder.requisites.buyerCompany?.innDescription"
          class="inn"
        >
          {{ localOrder.requisites.buyerCompany.innDescription }}
        </div>
      </div>

      <VSelectV2
        v-model="localOrder.requisites.buyerProductionSite"
        label="Производственная площадка"
        required
        :data="buyerProductionSiteSource"
      >
        <template #helper-text>
          <div v-if="localOrder?.isDraft">
            Выбранная площадка будет установлена на все позиции черновика заказа
          </div>
        </template>
      </VSelectV2>

      <VSelectV2
        v-model="localOrder.requisites.buyerStore"
        label="Склад доставки"
        :clearable="false"
        required
        :data="buyerStoreSource"
      />

    </section>

    <hr>

    <section>
      <header>Договор</header>
      <VFormItem>
        <VSelectV2
          v-model="localOrder.requisites.contract"
          :placeholder="!hasSeller ? 'Выберите партнера продавца' : 'Название или номер договора'"
          :data="contractSource"
        >
          <template #option="{ option }">
            {{ option.toString() }}
            <div class="description">
              {{ option?.sellerCompany?.name }}<br>
              {{ option?.buyerCompany?.name }}
            </div>
          </template>
        </VSelectV2>

        <VButton
          :disabled="$core.utils.isNullOrUndefined(localOrder.requisites.sellerCompany) === true || $core.utils.isNullOrUndefined(localOrder.requisites.buyerCompany) === true"
          @click="startCreateContract"
          material="add"
        />
      </VFormItem>

      <VSwitch
        v-if="localOrder.requisites.isVatPresented"
        v-model="localOrder.requisites.isVatIncludedSeller"
        :disabled="localOrder.requisites.isVatPresented === false"
        theme="slider"
        label="НДС"
        layout="vertical"
        checked-label="Включительно"
        unchecked-label="Сверху"
      />

      <VInput
        v-else
        label="НДС"
        layout="vertical"
        disabled
        value="Не облагается"
      />

      <VSelectV2
        v-model="localOrder.requisites.deliveryType"
        label="Тип доставки"
        required
        :data="DeliveryTypesService.getListForDirectOrders()"
      />

      <VSwitch
        v-model="localOrder.requisites.isDeliveryPaidByBuyer"
        theme="slider"
        checked-label="Покупатель оплачивает доставку"
        unchecked-label="Продавец доставляет за свой счёт"
      />
    </section>

    <hr>

    <PaymentTermEditor
      v-if="localOrder.requisites.isFreeSamples === false"
      :entity="localOrder"
    />

    <hr>

    <section>
      <VInputNumber
        v-model="localOrder.requisites.deliveryDays"
        label="Срок доставки, дней"
        layout="vertical"
        required
        :precision="0"
      />
    </section>

    <template v-if="localOrder.requisites.isPrinting">
      <hr>

      <section>
        <VDateInput
          v-model="localOrder.requisites.requestDate"
          label="Дата заявки"
          layout="vertical"
          required
        />
      </section>
    </template>

    <hr>

    <section v-if="localOrder.requisites.isPreorder === false">
      <VTextarea
        v-model="localOrder.notes"
        label="Комментарий поставщику"
        layout="vertical"
        clearable
      />
    </section>

    <section>
      <VSelectV2
        label="Ответственный"
        placeholder="Введите ФИО ответственного"
        v-model="localOrder.requisites.responsibleBuyer"
        required
        :data="responsibleSource"
      />
    </section>
  </VDialogV3>
</template>

<script lang="ts">
import PartnersService from '@/domains/Partners/PartnersService';
import ContractsExternalService from '@/domains/Contracts/ContractsExternalService';
import OrdersService from '@/domains/Orders/OrdersService';
import DeliveryTypesService from '@/domains/DeliveryTypes/DeliveryTypesService';
import PaymentTermEditor
  from '@/domains/PaymentTerms/components/PaymentTermEditor/PaymentTermEditor.vue';
import StoresService from "@/domains/Stores/StoresService";
import CompaniesService from "@/domains/Companies/CompaniesService";

import type Order from '@/domains/Orders/Order';
import ProductionSitesService from "@/domains/ProductionSites/ProductionSitesService";

export default defineComponent({
  name: 'OrderRequisites',

  components: {
    PaymentTermEditor,
  },

  props: {
    order: {
      type: Object as PropType<Order>,
      required: true,
    },
  },

  emits: ['updated', 'close'],

  setup() {
    return {
      PartnersService,
      DeliveryTypesService,
    };
  },

  data() {
    /**
     * Правило прелоада зависимых источников документа:
     * - новый — preloadOnMount: опции готовы до первого клика,
     *   контрагентские подгрузятся сами при выборе партнёра;
     * - существующий — preloadOnFocus: шанс, что реквизиты станут
     *   менять, низок — впустую не грузим.
     */
    return {
      sellerCompanySource: this.$core.loader.select<Order>(({seller}) =>
        CompaniesService.getList({ partnerId: seller.id })
      )
        .dependsOn({seller: () => this.localOrder.requisites.seller})
        .preloadOnMount(this.order.isExisting === false)
        .preloadOnFocus(this.order.isExisting === true),

      sellerStoreSource: this.$core.loader.select<Order>(({seller}) =>
        StoresService.getList({ partnerId: seller.id })
      )
        .dependsOn({seller: () => this.localOrder.requisites.seller})
        .preloadOnMount(this.order.isExisting === false)
        .preloadOnFocus(this.order.isExisting === true),

      buyerCompanySource: this.$core.loader.select<Order>(({buyer}) =>
        CompaniesService.getList({ partnerId: buyer.id })
      )
        .dependsOn({ buyer: () => this.localOrder.requisites.buyer })
        .default((options) => options.find(_option => _option.id === 66))
        .preloadOnMount(this.order.isExisting === false)
        .preloadOnFocus(this.order.isExisting === true),

      buyerStoreSource: this.$core.loader.select<Order>(({buyer}) =>
        StoresService.getList({partnerId: buyer.id})
      )
        .dependsOn({
          buyer:        () => this.localOrder.requisites.buyer,
          buyerCompany: () => this.localOrder.requisites.buyerCompany,
        })
        .default((options, {buyerCompany}) =>
          options.find(_option => _option.id === buyerCompany?.defaultStore?.id)
        )
        .preloadOnMount(this.order.isExisting === false)
        .preloadOnFocus(this.order.isExisting === true),

      buyerProductionSiteSource: this.$core.loader.select<Order>(({buyerCompany}) =>
        ProductionSitesService.getList(null, buyerCompany)
      )
        .dependsOn({buyerCompany: () => this.localOrder.requisites.buyerCompany})
        .default((options) => options.find(_option => _option.id === "berdsk"))
        .preloadOnMount(this.order.isExisting === false)
        .preloadOnFocus(this.order.isExisting === true),

      contractSource: this.$core.loader.select<Order>(({sellerCompany, buyerCompany}) =>
        ContractsExternalService.getList({ sellerCompany, buyerCompany })
      )
        .dependsOn({
          buyerCompany: () => this.localOrder.requisites.buyerCompany,
          sellerCompany: () => this.localOrder.requisites.sellerCompany,
        })
        .preloadOnMount(this.order.isExisting === false)
        .preloadOnFocus(this.order.isExisting === true),

      responsibleSource: this.$core.loader.responsible(),

      localOrder: this.$core.utils.cloneDeep(this.order),
    };
  },

  computed: {
    hasSeller(): boolean {
      return this.$core.utils.isObject(this.localOrder.requisites.seller);
    },

    orderCategoriesOptions() {
      return OrdersService.orderCategories.filter(category =>
        category.id !== 'printing' && category.id !== 'namanganMain',
      );
    },

    /** блокируем сабмит пока данные не загрузаться в поля,
     * чтобы во время await не засабмитились старые значения,
     * особенно важно для не-required полей, т.к. vform их пропустит*/

    isSourcesLoading(): boolean {
      return this.sellerCompanySource.isLoading
        || this.sellerStoreSource.isLoading
        || this.buyerCompanySource.isLoading
        || this.buyerStoreSource.isLoading
        || this.buyerProductionSiteSource.isLoading
        || this.contractSource.isLoading
        || this.responsibleSource.isLoading;
    },
  },

  methods: {
    async startCreateContract(): Promise<void> {
      // сначала ждём когда пользователь создаст договор
      // стороны и их юр. лица берутся из реквизитов заказа
      const created = await ContractsExternalService.createContractFromOrderRequisites(this.localOrder.requisites);

      if (this.$core.utils.isNullOrUndefined(created) === false) {
        // потом перезагружаем источник селекта — новый договор появится в списке,
        // а его опции и есть тот самый список по юр. лицам продавца и покупателя
        await this.contractSource.load();

        // созданный договор находим в списке по id
        const contract = this.contractSource.options.find(option => option.id === created!.id);

        if (this.$core.utils.isNullOrUndefined(contract) === false) {
          this.localOrder.requisites.contract = contract;
        }
        else {
          // в списке не нашёлся — выбор оставляем пользователю
        }
      }
      else {
        // карточку договора закрыли без сохранения — ничего не меняем
      }
    },

    // ==================== Сохранение ====================

    async onSubmit(): Promise<void> {
      this.$emit('updated', this.localOrder);
    },



  },
});
</script>